import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { BACKOFFICE_LANGUAGES, Language } from '@loyalty/shared-common';
import { LanguageAttributes } from '@loyalty/shared-common/gamification-api-client';
import { Loader } from '../shared/layout/Loader';

const defaultLanguage = { name: 'English', isoCode: 'en' };

interface LanguagesContextInterface {
  languages: LanguageAttributes[];
  currentLanguage: LanguageAttributes;
  setLanguages: (languages: LanguageAttributes[]) => void;
  setCurrentLanguage: (language: LanguageAttributes) => void;
}

export const LanguagesContext = createContext<LanguagesContextInterface>({
  languages: [],
  currentLanguage: defaultLanguage,
  setLanguages: () => undefined,
  setCurrentLanguage: () => undefined,
});

export function useLanguages() {
  return useContext(LanguagesContext);
}

export function LanguagesProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [languages, setLanguages] = useState<LanguageAttributes[]>(
    Object.values(BACKOFFICE_LANGUAGES),
  );
  const [currentLanguage, _setCurrentLanguage] = useState<LanguageAttributes>(undefined as any);

  const setCurrentLanguage = useCallback((language: LanguageAttributes) => {
    if (!currentLanguage || currentLanguage.isoCode !== language.isoCode) {
      localStorage.setItem('currentLanguage', language.isoCode);
      _setCurrentLanguage(language);
    }
  }, []);

  const context = useMemo(
    () => ({
      languages,
      currentLanguage,
      setLanguages,
      setCurrentLanguage,
    }),
    [languages, currentLanguage, setCurrentLanguage],
  );

  useEffect(() => {
    const lastLanguageUsed = (localStorage.getItem('currentLanguage') || 'en') as Language;
    const language = BACKOFFICE_LANGUAGES[lastLanguageUsed];
    setCurrentLanguage(language ? language : defaultLanguage);
    setLoading(false);
  }, [setCurrentLanguage]);

  return loading ? (
    <Loader />
  ) : (
    <LanguagesContext.Provider value={context}>{children}</LanguagesContext.Provider>
  );
}
